import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, ChevronDown, LogOut, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/** Header auth control: a "Log in" button or a logged-in user menu. */
export default function AuthMenu({
  variant,
  onNavigate,
}: {
  variant: 'desktop' | 'mobile';
  onNavigate?: () => void;
}) {
  const { session, loading, openAuth, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (loading) return null;

  const handleLogout = async () => {
    setOpen(false);
    onNavigate?.();
    await logout();
    navigate('/');
  };

  // Logged out → a single login button.
  if (!session || session.role === 'unknown') {
    const onClick = () => {
      onNavigate?.();
      openAuth('login');
    };
    if (variant === 'mobile') {
      return (
        <button
          onClick={onClick}
          className="hover:text-accent text-fg-secondary text-left text-sm font-medium transition-colors"
        >
          Log in
        </button>
      );
    }
    return (
      <button
        onClick={onClick}
        className="hover:text-accent text-fg-secondary cursor-pointer text-sm font-medium transition-colors"
      >
        Log in
      </button>
    );
  }

  // Owner → a dashboard shortcut.
  if (session.role === 'owner') {
    if (variant === 'mobile') {
      return (
        <>
          <Link
            to="/admin"
            onClick={onNavigate}
            className="hover:text-accent text-fg-secondary text-sm font-medium transition-colors"
          >
            Dashboard
          </Link>
          <button
            onClick={handleLogout}
            className="hover:text-accent text-fg-secondary text-left text-sm font-medium transition-colors"
          >
            Log out
          </button>
        </>
      );
    }
    return (
      <div className="flex items-center gap-4">
        <Link
          to="/admin"
          className="hover:text-accent text-fg-secondary text-sm font-medium transition-colors"
        >
          Dashboard
        </Link>
        <button
          onClick={handleLogout}
          className="hover:text-accent text-fg-secondary text-sm font-medium transition-colors"
        >
          Log out
        </button>
      </div>
    );
  }

  // Customer.
  const name = session.customer?.name || 'Account';
  const firstName = name.split(' ')[0];

  if (variant === 'mobile') {
    return (
      <>
        <Link
          to="/account/bookings"
          onClick={onNavigate}
          className="hover:text-accent text-fg-secondary text-sm font-medium transition-colors"
        >
          My bookings
        </Link>
        <Link
          to="/account"
          onClick={onNavigate}
          className="hover:text-accent text-fg-secondary text-sm font-medium transition-colors"
        >
          Account
        </Link>
        <button
          onClick={handleLogout}
          className="hover:text-accent text-fg-secondary text-left text-sm font-medium transition-colors"
        >
          Log out
        </button>
      </>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="hover:text-accent text-fg-secondary flex items-center gap-1.5 text-sm font-medium transition-colors"
      >
        <User className="h-4 w-4" />
        {firstName}
        <ChevronDown className="text-fg-subtle h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="border-subtle bg-surface absolute right-0 mt-2 w-48 overflow-hidden rounded-xl border py-1 shadow-lg">
          <Link
            to="/account/bookings"
            onClick={() => setOpen(false)}
            className="text-fg-secondary hover:bg-canvas flex items-center gap-2 px-4 py-2 text-sm"
          >
            <Calendar className="text-fg-subtle h-4 w-4" />
            My bookings
          </Link>
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            className="text-fg-secondary hover:bg-canvas flex items-center gap-2 px-4 py-2 text-sm"
          >
            <User className="text-fg-subtle h-4 w-4" />
            Account
          </Link>
          <button
            onClick={handleLogout}
            className="text-fg-secondary hover:bg-canvas flex w-full items-center gap-2 px-4 py-2 text-left text-sm"
          >
            <LogOut className="text-fg-subtle h-4 w-4" />
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
